import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** localStorage key holding the last resumable in-app route. */
export const LAST_ROUTE_KEY = 'LAST_ROUTE';

/**
 * Signed-in app sections worth resuming into. Excludes marketing/auth/profile-picker/
 * onboarding/invite routes by omission, so an unknown or transient route never gets stored.
 */
const RESUMABLE_ROOTS = [
  '/family',
  '/child',
  '/therapist',
  '/subscriptions',
  '/account',
  '/admin',
  '/documentation',
];

/**
 * Persists the last meaningful in-app route so an installed PWA can resume there
 * on cold launch instead of bouncing through the profile picker. Activate it by
 * injecting it once at the app root; {@link resumeLastRoute} reads the stored value.
 */
@Injectable({ providedIn: 'root' })
export class LastRouteService {
  #router = inject(Router);

  constructor() {
    this.#router.events
      .pipe(
        takeUntilDestroyed(),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        if (!RESUMABLE_ROOTS.some((root) => url === root || url.startsWith(`${root}/`))) return;
        localStorage.setItem(LAST_ROUTE_KEY, url);
      });
  }
}
